/**
 * Recorder-liveness classification shared by the recording-start convergence
 * (APP-6140 zombie takeover) and the DSK-695 post-reload recovery, so both
 * decide "is this recorder actually capturing?" from the same rules.
 *
 * Extracted from `useMutateStartTauriRecording.ts` — history:
 * `git log --follow src/hooks/mutation/useMutateStartTauriRecording.ts`.
 */
import { type RecordingLiveness } from 'src-tauri/src/bindings/RecordingLiveness';

// A non-Stopped recorder is a zombie when its data cursor has stalled while
// Recording, or it has run implausibly long. The desktop-mic transport stamp is
// not a reliable liveness signal -- a zombie still carries it -- so classify on
// the recorder's own liveness snapshot instead.
// 3× the ~45s chunk cadence (HAR-1327), past jitter / delayed PCM stamps.
export const STALE_DATA_THRESHOLD_SECS = 135;
export const HARD_CAP_ELAPSED_SECS = 4 * 60 * 60; // a real consult never runs this long

/**
 * Classify a non-Stopped recorder as a zombie (safe to force-stop and take over)
 * vs a live recording (leave alone). Anything ambiguous returns not-a-zombie so
 * the caller keeps the safe fallback (skip convergence, let start() reject).
 */
export const classifyRecorderLiveness = (
  liveness: RecordingLiveness
): { isZombie: boolean; reason: 'stale_data' | 'hard_cap' | null } => {
  if (liveness.state === 'Stopped') {
    return { isZombie: false, reason: null };
  }
  if (liveness.elapsed_secs > HARD_CAP_ELAPSED_SECS) {
    return { isZombie: true, reason: 'hard_cap' };
  }
  // A stale cursor is evidence of death only while Recording -- a Paused
  // recorder's cursor legitimately stalls, so Paused is capped by hard_cap
  // alone. null = data clock unknown (e.g. before the first advance).
  if (
    liveness.state === 'Recording' &&
    liveness.secs_since_last_data !== null &&
    liveness.secs_since_last_data > STALE_DATA_THRESHOLD_SECS
  ) {
    return { isZombie: true, reason: 'stale_data' };
  }
  return { isZombie: false, reason: null };
};
